/** @jsxImportSource @emotion/react */
import React from 'react';
import logo from '../../assets/logo.svg';
import { keyframes } from '@emotion/react';

// Define the animation
const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;

// Add pulsing animation for the CTA buttons
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

function YouTubeShortsAccountSetupComponent() {
  return (
    <div className="min-h-screen bg-[#7B1C14] py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="Spicy Cubes" 
            className="h-16 sm:h-20 md:h-24 mx-auto"
          />
        </div>
        
        <div className="bg-[#6E1912] rounded-lg shadow-lg p-8">
          <div className="relative pb-6 mb-6 border-b border-[#F7E8CC]/10">
            <h1 className="text-3xl font-bold text-[#F7E8CC] text-center">SOP: YouTube Shorts Account Setup</h1>
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-[2px] bg-gradient-to-r from-transparent via-[#F7E8CC]/10 to-transparent" />
          </div>

          {/* Introduction */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-[#F7E8CC] font-medium">
              Follow this step-by-step guide to set up your YouTube Shorts account for Spicy Cubes content. 
              A properly set up account will help you maximize reach and engagement.
            </p>
          </div>

          {/* Step 1 */}
          <div className="mt-6 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                1
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Sign Up</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90">
                Create a <span className="font-bold text-[#FFD700]">BRAND NEW</span> YouTube channel for your Spicy Cubes content.
              </p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                2
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Username</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-2">
                Username examples:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-[#F7E8CC]/90">
                <li>tryspicycubes</li>
                <li>needspicycubes</li>
                <li>takespicycubes</li>
              </ul>
              <p className="mt-2 text-[#F7E8CC]/90">
                If you need help with your username, please reach out on Geneva.
              </p>
              <div className="mt-3 p-2 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30">
                <p className="text-[#FFD700] font-medium">TIP: Include "Spicy" in your display name</p>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                3
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Profile Photo</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90">
                Use Spicy Cubes "S" logo.
              </p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                4
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Bio</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-3">
                You will need to edit your channel description & links from YouTube Studio on a computer.
              </p>
              <p className="text-[#F7E8CC]/90 mb-2">
                Description example:
              </p>
              <div className="p-3 bg-[#5E150F]/50 rounded-md border border-[#FFD700]/30 font-medium text-[#F7E8CC]">
                Spicy cubes for two 💞<br/>
                #spicypartner<br/>
              </div>
              <p className="mt-3 text-[#F7E8CC]/90">
                <span className="font-bold">Add your affiliate link to the links section</span>
              </p>
            </div>
          </div>

          {/* Step 5 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                5
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Upload</h3>
            </div>
            <div className="ml-11">
              <ul className="list-disc pl-5 space-y-2 text-[#F7E8CC]/90">
                <li>Upload your first video as a Short</li>
                <li><span className="text-[#FFD700] font-medium">Important:</span> If your video contains background music from TikTok, you will need to upload without the music or you could receive a copyright strike</li>
                <li>If you want to add background music, add a trending sound from YouTube with the volume lowered</li>
              </ul>
            </div>
          </div>

          {/* Step 6 */}
          <div className="mt-4 bg-[#5E150F] p-5 rounded-md">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 rounded-full bg-[#FFD700] flex items-center justify-center text-[#5E150F] font-bold mr-3">
                6
              </div>
              <h3 className="font-bold text-lg text-[#FFD700]">Verify Identity</h3>
            </div>
            <div className="ml-11">
              <p className="text-[#F7E8CC]/90 mb-3">
                YouTube will allow you to add a link to the description of each video in YouTube Studio <span className="font-bold text-[#FFD700]">BUT</span> you need to verify your identity first. <span className="italic">*This can take 24 hours or more to process*</span>
              </p>
              
              <div className="p-3 bg-[#5E150F]/50 rounded-md mb-3">
                <p className="font-medium text-[#F7E8CC] mb-2">To verify your identity:</p>
                <ol className="list-decimal pl-5 space-y-1 text-[#F7E8CC]/90">
                  <li>Login to your account from a computer</li>
                  <li>Click your profile photo on the right hand side of your screen</li>
                  <li>Toggle down to 'YouTube Studio'</li>
                  <li>On the left side of the screen click 'Content'</li>
                  <li>Go to details and click the pencil 'Details' icon</li>
                  <li>Copy your affiliate link to the description</li>
                  <li>You will see "To make external links clickable, first complete a one-time verification"</li>
                  <li>Click it and follow steps to verify</li>
                </ol>
              </div>
              
              <p className="text-[#F7E8CC]/90 font-medium">
                Continue posting until verified. Once verified, you will need to access YouTube Studio to add links in each Shorts description section.
              </p>
            </div>
          </div>

          {/* Help Section */}
          <div className="mt-6 bg-[#5E150F]/30 p-4 rounded-md border border-[#FFD700]/30">
            <p className="text-center text-[#F7E8CC]/90">
              If you have any questions regarding account setup, reach out to Ashli on Geneva.
            </p>
          </div>
          
          <div className="mt-8 text-center">
            <a 
              href="/content-brief" 
              css={{
                animation: `${pulse} 2s ease-in-out infinite`,
              }}
              className="inline-flex justify-center rounded-md bg-[#FFB700] py-3 px-6 text-lg font-bold text-[#5E150F] shadow-sm hover:bg-[#FFA200] focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:ring-offset-2 focus:ring-offset-[#7B1C14] transition-colors duration-200"
            >
              📱 Back to Content Brief 📱
            </a>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-[#F7E8CC]/80 text-center">
            Questions? Contact{' '}
            <a 
              href="mailto:ashli@spicycubes.co" 
              className="font-bold text-[#F7E8CC] hover:text-[#F7E8CC] whitespace-nowrap underline decoration-[2px] hover:decoration-[2.5px] transition-all duration-200 [text-decoration-skip-ink:none]"
            >
              ashli@spicycubes.co
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export const YouTubeShortsAccountSetup = YouTubeShortsAccountSetupComponent; 